import { createContext, useContext, useState, useEffect } from 'react';
import authAPI from '../api/authAPI';

const AuthContext = createContext(null);

const SESSION_CHECK_INTERVAL = 60000;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isGuest, setIsGuest] = useState(false);

  useEffect(() => {
    checkSession();
  }, []);

  useEffect(() => {
    if (!user || isGuest) return;

    const interval = setInterval(() => {
      checkSession();
    }, SESSION_CHECK_INTERVAL);

    return () => clearInterval(interval);
  }, [user, isGuest]);

  const checkSession = async () => {
    try {
      const data = await authAPI.checkSession();
      if (data.authenticated) {
        setUser({ ...data.user, is_admin: data.user.is_staff });
        setIsGuest(false);
      } else {
        setUser(null);
        setIsGuest(false);
      }
    } catch (error) {
      console.error('Session check failed:', error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const register = async (userData) => {
    try {
      const data = await authAPI.register(userData);
      setUser({ ...data.user, is_admin: data.user.is_staff });
      setIsGuest(false);
      return data;
    } catch (error) {
      throw error;
    }
  };

  const login = async (username, password) => {
    try {
      const data = await authAPI.login(username, password);
      setUser({ ...data.user, is_admin: data.user.is_staff });
      setIsGuest(false);
      return data;
    } catch (error) {
      throw error;
    }
  };

  const logout = async () => {
    try {
      await authAPI.logout();
      setUser(null);
      setIsGuest(false);
    } catch (error) {
      console.error('Logout failed:', error);
      setUser(null);
      setIsGuest(false);
    }
  };

  const continueAsGuest = () => {
    setUser(null);
    setIsGuest(true);
  };

  const value = {
    user,
    loading,
    register,
    login,
    logout,
    continueAsGuest,
    isAuthenticated: !!user,
    isGuest: isGuest,
    isLockedOut: false,
    refreshUser: checkSession
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
